import React, { Component } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, FormControl, Select, MenuItem, Typography, Box,
    FormLabel, InputLabel
} from "@mui/material";
import AlertDialogPopop from "../AlertDialogPopop/AlertDialogPopop";
import { ShowAlert } from "../ShowAlert";
import UserAndPermissionService from "../../Services/UserAndPermissionService";
import CommonPermissonComp from "./CommonPermissonComp";
import { ISupportStaffs } from "../../Pages/UserDetails/Interface";
import { Permissions } from "./PermissionsInterface.";
import { getFXReportDefaultPermissions } from '../Reusables/Utility';

interface UpdateUserPermissionsProps {
    open: boolean;
    onClose?: (state: boolean, action: string) => void;
    accessToken: string;
    selectedSupportStaff: ISupportStaffs
    refreshData?: () => void
}

export enum ActionButtons {
    Save = 'save',
    Cancel = 'cancel'
}

interface Role {
    role_id: string;
    role_name: string;
}

interface UpdateUserPermissionsState {
    isSavePermissions: boolean;
    roleList: Role[];
    selectedRoleName: string;
    selectedRoleID: string;
    permissions: Permissions;
    isPermissionLoading: boolean,
    selectedUserID: string
}

// Main class to update user role....
class UpdateUserPermissions extends Component<UpdateUserPermissionsProps, UpdateUserPermissionsState> {

    constructor(props: UpdateUserPermissionsProps) {
        super(props);
        this.state = {
            roleList: [],
            selectedRoleName: this.props.selectedSupportStaff?.roleName ?? "",
            selectedRoleID: this.props.selectedSupportStaff?.role_id ?? "",
            selectedUserID: this.props.selectedSupportStaff?.user_id ?? "",
            permissions: {
                profileSettings: {
                    isGeneralSettingsVisible: false,
                    isGeneralSettingSaveBtnVisible: false,
                    isExchangeRateVisible: false,
                    isExchangeRateSaveBtnVisible: false,
                    isPauseTransactionVisible: false,
                    isCardVerification: false,
                },
                dashboard: {
                    isVisible: true,
                    isHeaderVisible: false,
                    isTransactionMetricsVisible: false,
                    isFinancialMetricsVisible: false,
                    isRiskMetricsVisible: false,
                    isSupportMetricsVisible: false,
                    isPerformanceMetricsVisible: false,
                },
                transactionLogs: {
                    isVisible: true,
                    isDownloadVisible: false,
                    isFraudMarkVisible: false,
                    isRefundVisible: false,
                },
                userLogs: {
                    isVisible: true,
                },
                complianceLogs: {
                    isVisible: true,
                    isZendeskNavigationAllowed: false,
                    isTransactionNavigationAllowed: false,
                },
                userDetails: {
                    isVisible: false,
                    isVisibleAdminsTab: false,
                    isVisibleMobileUsersTab: false,
                    isVisibleSupportStaffsTab: false,
                    isBlockUserVisible: false,
                    isUserSettingsVisible: false,
                    isUserSettingSaveBtnVisible: false,
                    isStatusHistoryVisible: false,
                    isWalletHistoryTabVisible: false,
                    isUpdatePermissionVisible: false,
                    isDownloadVisibleAdmins: false,
                    isDownloadVisibleMobileUsers: false,
                    isDownloadVisibleSupportStaffs: false,
                    isEditUserInfoVisible: false,
                    isAddUserVisible: false
                },
                userTransactionReport: {
                    isVisible: false,
                    isDownloadVisible: false,
                    isLimitHistoryVisible: false,
                    isLimitHistoryDownloadVisible: false,
                },
                promoCode: {
                    isVisible: false,
                    isDownloadVisiblePromoCode: false,
                    isDownloadVisibleCashbackCoupon: false,
                    isVisiblePromoCodeTab: false,
                    isVisibleCouponCodeTab: false,
                    isAddPromoCodeVisible: false,
                    isAddCouponCodeVisible: false,
                    isBlockCodeVisible: false,
                    isActiveCodeVisible: false
                },
                exchangeRates: {
                    isVisible: false,
                    isDownloadVisible: false,
                    isAddCurrencyPairVisible: false,
                    isEditRatesVisible: false,
                    isEditPaymentMethodVisible: false,
                    isActiveCurrencyAllowed: false,
                    isInactiveCurrencyAllowed: false,
                },
                referrals: {
                    isVisible: false,
                    isDownloadVisible: false,
                    isReferralSettingsVisible: false,
                    isSaveSettingsAllowed: false,
                    isViewRefereesVisible: false,
                    isTransactionNavigationAllowed: false
                },
                chargeBack: {
                    isVisible: false,
                    isDownloadFileAllowed: false,
                    isSendEmailAllowd: false
                },
                partner: {
                    isVisible: false,
                    isVisibleLiquidityTab: false,
                    isVisiblePayoutTab: false,
                    isDownloadVisibleLiquidity: false,
                    isDownloadVisiblePayout: false,
                    isAddVisibleLiquidity: false,
                    isAddVisiblePayout: false,
                    isEditPartnersVisible: false,
                    isActivePartnersVisible: false,
                    isInActivePartnersVisible: false,
                },
                ratereconciliation: {
                    isVisible: false,
                },
                fxReports: getFXReportDefaultPermissions(false),
                fxAllocation: {
                    isVisible: false,
                    isCreatePurchaseVisible: false,
                    isCreateLiquidityVisible: false,
                    isCreatePartnerVisible: false
                },
            },
            isSavePermissions: false,
            isPermissionLoading: false,
        }
    }

    componentDidMount() {
        this.getUserRoles();
    }

    // getuser role and update appropriate permission
    private async getUserRoles() {
        try {
            const response = await UserAndPermissionService.getUserRoles(this.props.accessToken);
            if (!response.ok) {
                throw new Error("Failed to fetch user roles");
            }

            const data = await response.json();
            this.setState(
                { roleList: data.roleList || [] },
                () => {
                    const selectedRoleName = this.state.selectedRoleName;
                    if (data.roleList?.length > 0 && selectedRoleName) {
                        this.handleRoleSelection(selectedRoleName);
                    }
                }
            );
        } catch (error) {
            ShowAlert("Error", "Failed to fetch user roles. Please try again later.", "error");
            console.error("Error fetching user roles:", error);
        }
    }

    //Update permission based on role selection
    handleRoleSelection = async (e: any | string) => {
        let selectedRoleName = ""

        if (typeof e === "string") {
            selectedRoleName = e; // Direct call with role name
        } else {
            selectedRoleName = e.target.value; // Event-based call
        }

        const selectedRole = this.state.roleList.find(role => role.role_name === selectedRoleName);
        if (!selectedRole) return;
        this.setState({
            selectedRoleName: selectedRole.role_name,
            selectedRoleID: selectedRole.role_id,
            isPermissionLoading: true
        });

        try {
            const response = await UserAndPermissionService.getPermissions(this.props.accessToken, selectedRole.role_id);

            if (!response.ok) throw new Error("Failed to fetch role details");

            const data = await response.json();
            console.log("Role details:", data);

            this.setState({ isPermissionLoading: false, permissions: data?.permissions, });
        } catch (error) {
            console.error("Error fetching role details:", error);
            this.setState({ isPermissionLoading: false });
        }
    };

    private async handleSave(action: string) {
        const { selectedRoleID, selectedUserID } = this.state;

        if (action === ActionButtons.Save) {
            try {
                let postData = {
                    supportStaffAssign: {
                        roleId: selectedRoleID,
                        userId: [selectedUserID],
                    },
                };

                const rsp = await UserAndPermissionService.setUsersRole(this.props.accessToken, postData);
                if (rsp.status === 200) {
                    ShowAlert("Success", "User role updated successfully!", "success");
                    this.props.refreshData?.();
                } else {
                    throw new Error();
                }
            } catch (error) {
                console.error("Error updating user Creation:", error);
                ShowAlert("Error", "Failed to update user role. Please try again.", "error");
            }
        } else if (this.props.onClose) {
            this.props.onClose(false, action);
        }
    }

    handlePermissionsChange = (updatedPermissions: any) => {
        this.setState({ permissions: updatedPermissions });
    };

    render() {
        const { open } = this.props;
        const { isSavePermissions, selectedRoleName } = this.state;
        const isSaveDisabled = !selectedRoleName;

        return (
            <Dialog open={open}
                onClose={() => this.handleSave(ActionButtons.Cancel)}
                fullWidth
                maxWidth="md"
            >
                <DialogTitle>
                    Update Role
                    <Box
                        sx={{
                            padding: "10px",
                            border: "1px solid #ddd",
                            borderRadius: "5px",
                            backgroundColor: "#f9f9f9",
                            marginTop: "10px"
                        }}
                    >
                        <Typography variant="body1" >
                            Name: Rajesh Rajendran
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Email: Rajesh.r@congruentindia.com
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Mobile: +1123456789
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box style={{ display: "flex", gap: "25px", width: "100%" }}>
                        {/* Left Column - User Info & Role Selection */}
                        <Box style={{ flex: 1, display: "flex", flexDirection: "column", gap: "10px" }}>
                            <FormControl sx={{ marginTop: "25px" }}>
                                <InputLabel id="role-select-label">Current Role</InputLabel>
                                <Select
                                    value={this.state.selectedRoleName || ""}
                                    displayEmpty
                                    renderValue={(selected) => selected || "Select a role"}
                                    onChange={this.handleRoleSelection}
                                    label="Current Role"
                                >
                                    {this.state.roleList.map((role) => (
                                        <MenuItem key={role.role_id} value={role.role_name}>
                                            {role.role_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                        {/* Right Column - Permissions Component */}
                        <Box style={{ flex: 1 }}>
                            <CommonPermissonComp
                                permissions={this.state.permissions}
                                onPermissionsChange={this.handlePermissionsChange}
                                isPermissionLoading={this.state.isPermissionLoading}
                                isReadOnly={true}
                            />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => this.handleSave(ActionButtons.Cancel)}>
                        Cancel</Button>
                    <Button
                        color="secondary"
                        variant="contained"
                        disabled={isSaveDisabled}
                        onClick={() => this.setState({ isSavePermissions: true })}>
                        Save
                    </Button>
                </DialogActions>
                <AlertDialogPopop
                    open={isSavePermissions}
                    alertTitle="Confirmation"
                    alertBody="Are you sure you want to update role?"
                    onClose={(_, action_) => {
                        this.setState({ isSavePermissions: false }, () => {
                            if (action_ === "ok") {
                                this.handleSave(ActionButtons.Save);
                            }
                        });
                    }} />
            </Dialog>
        );
    }
}

export default UpdateUserPermissions;
